/**
 * <modal-dialog> / <modal-opener>: generic modal base (window.ModalDialog), and <product-modal>: product media zoom.
 */
if (!customElements.get("modal-dialog")) {
  class ModalDialog extends HTMLElement {
    constructor() {
      super();
      this.querySelector('[id^="ModalClose-"]').addEventListener(
        "click",
        this.hide.bind(this)
      );
      this.addEventListener("keyup", (event) => {
        if (event.code.toUpperCase() === "ESCAPE") {
          this.hide();
        }
      });
      if (this.classList.contains("media-modal")) {
        this.addEventListener("pointerup", (event) => {
          if (
            event.pointerType === "mouse" &&
            !event.target.closest("deferred-media, product-model")
          ) {
            this.hide();
          }
        });
      } else {
        this.addEventListener("click", (event) => {
          if (event.target.nodeName === "MODAL-DIALOG") {
            this.hide();
          }
        });
      }
    }

    connectedCallback() {
      if (this.moved || Shopify.designMode) {
        return;
      }

      this.moved = true;
      document.body.appendChild(this);
    }

    show(opener) {
      this.openedBy = opener;
      document.body.classList.add("overflow-hidden");
      this.setAttribute("open", "");
      trapFocus(this, this.querySelector('[role="dialog"]'));
      window.pauseAllMedia();
    }

    hide() {
      document.body.classList.remove("overflow-hidden");
      this.removeAttribute("open");
      if (this.openedBy) {
        removeTrapFocus(this.openedBy);
      }
      window.pauseAllMedia();
    }
  }

  window.ModalDialog = ModalDialog;
  customElements.define("modal-dialog", ModalDialog);

  // Global modal opener
  //
  class ModalOpener extends HTMLElement {
    constructor() {
      super();

      const button = this.querySelector("button");

      if (!button) {
        return;
      }

      button.addEventListener("click", () => {
        const modal = document.querySelector(this.getAttribute("data-modal"));

        if (modal) {
          modal.show(button);
        }
      });
    }
  }

  customElements.define("modal-opener", ModalOpener);
}

if (!customElements.get("product-modal")) {
  customElements.define(
    "product-modal",
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.closeEvent = new CustomEvent("product:modal-close");
        this.openEvent = new CustomEvent("product:modal-open");
        this.zoomLevel = 1;
        this.zoomStep = 0.25;
        this.minZoom = 0.25;
        this.maxZoom = 3;
      }

      createZoomControls() {
        if (this.zoomControls) return;

        const controls = document.createElement("div");
        controls.className = "product-media-modal-zoom-controls";
        controls.innerHTML = `
          <button type="button" class="product-media-modal-zoom-btn" data-zoom="out" aria-label="Zoom out">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M4 9h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </button>
          <span class="product-media-modal-zoom-level">100%</span>
          <button type="button" class="product-media-modal-zoom-btn" data-zoom="in" aria-label="Zoom in">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 4v10M4 9h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </button>
        `;

        controls.querySelector('[data-zoom="out"]').addEventListener("click", (e) => {
          e.stopPropagation();
          this.zoom(-this.zoomStep);
        });

        controls.querySelector('[data-zoom="in"]').addEventListener("click", (e) => {
          e.stopPropagation();
          this.zoom(this.zoomStep);
        });

        controls.addEventListener("pointerup", (e) => {
          e.stopPropagation();
        });

        this.zoomControls = controls;
        this.zoomLevelDisplay = controls.querySelector(".product-media-modal-zoom-level");
        this.querySelector(".product-media-modal-dialog").appendChild(controls);
      }

      zoom(delta) {
        this.zoomLevel = Math.min(
          this.maxZoom,
          Math.max(this.minZoom, Math.round((this.zoomLevel + delta) * 100) / 100),
        );
        this.applyZoom();
      }

      applyZoom() {
        const content = this.querySelector(".product-media-modal-content");
        if (content) {
          content.style.setProperty("--zoom-level", this.zoomLevel);
        }
        if (this.zoomLevelDisplay) {
          this.zoomLevelDisplay.textContent = Math.round(this.zoomLevel * 100) + "%";
        }
      }

      resetZoom() {
        this.zoomLevel = 1;
        this.applyZoom();
      }

      setAccessibilityWidgetVisible(visible) {
        const selectors = [
          '[id*="barrierefreie"]',
          '[class*="barrierefreie"]',
          'iframe[src*="barrierefreie"]',
        ];
        selectors.forEach((selector) => {
          document.querySelectorAll(selector).forEach((el) => {
            if (!this.contains(el)) {
              el.style.setProperty("display", visible ? "" : "none", "important");
            }
          });
        });

        document
          .querySelectorAll(
            'body > div[style*="position: fixed"], body > iframe[style*="position: fixed"]',
          )
          .forEach((el) => {
            if (!this.contains(el) && !el.closest("product-modal") && !el.closest("header")) {
              const rect = el.getBoundingClientRect();
              if (rect.bottom > window.innerHeight * 0.7 && rect.right > window.innerWidth * 0.7) {
                el.style.setProperty("display", visible ? "" : "none", "important");
              }
            }
          });
      }

      hide() {
        super.hide();
        this.resetZoom();
        this.setAccessibilityWidgetVisible(true);
        document.dispatchEvent(this.closeEvent);
      }

      show(opener) {
        super.show(opener);
        if (!this.zoomControls) {
          this.createZoomControls();
        }
        this.showActiveMedia();
        this.setAccessibilityWidgetVisible(false);
        document.dispatchEvent(this.openEvent);
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute("data-media-id")}"])`,
        ).forEach((element) => {
          element.classList.remove("is-active");
        });
        const activeMedia = this.querySelector(
          `[data-media-id="${this.openedBy.getAttribute("data-media-id")}"]`,
        );
        const activeMediaTemplate = activeMedia.querySelector("template");
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add("is-active");
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == "DEFERRED-MEDIA" &&
          activeMediaContent &&
          activeMediaContent.querySelector(".js-youtube")
        ) {
          activeMedia.loadContent();
        }
      }
    },
  );
}
